import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";

import { FormField } from "@/components/ui/FormField";

import {
  createSpecialOffer,
  updateSpecialOffer,
} from "@/services/offers";
import DatePicker from "../ui/DatePicker";

// ─── Zod Schema ───────────────────────────────────────────────────────────────

const offerSchema = z
  .object({
    description: z
      .string()
      .min(1, "Description is required")
      .min(3, "Description must be at least 3 characters"),

    valid_from: z
      .string()
      .min(1, "Valid From date is required"),

    valid_to: z
      .string()
      .min(1, "Valid To date is required"),

    discount_amount: z
      .string()
      .min(1, "Discount Amount is required")
      .refine((val) => !isNaN(Number(val)), {
        message: "Enter a valid number",
      })
      .refine((val) => Number(val) > 0, {
        message: "Amount must be greater than 0",
      }),
  })
  .refine(
    (data) => {
      if (!data.valid_from || !data.valid_to) return true;
      return data.valid_to > data.valid_from;
    },
    {
      message: "Valid To must be after Valid From",
      path: ["valid_to"],
    }
  );

type OfferFormData = z.infer<typeof offerSchema>;

// ─── Props ────────────────────────────────────────────────────────────────────

interface OfferModalProps {
  offer?: any;
  onClose: () => void;
  onSuccess: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SpecialOfferModal({
  offer,
  onClose,
  onSuccess,
}: OfferModalProps) {
  const {
    control,
    handleSubmit,
    reset,
    watch,setValue,
    formState: { isSubmitting, errors },
  } = useForm<OfferFormData>({
    resolver: zodResolver(offerSchema),
    defaultValues: {
      description: "",
      valid_from: "",
      valid_to: "",
      discount_amount: "",
    },
  });

  const validFrom = watch("valid_from");
const skipNextClear = useRef(false); 
  useEffect(() => {
    skipNextClear.current = true;
    if (!offer) {
      reset({
        description: "",
        valid_from: "",
        valid_to: "",
        discount_amount: "",
      });
      return;
    }

    reset({
      description: offer.description || "",
      valid_from: offer.valid_from
        ? offer.valid_from.split("T")[0]
        : "",
      valid_to: offer.valid_to
        ? offer.valid_to.split("T")[0]
        : "",
      discount_amount: offer.discount_amount
        ? String(offer.discount_amount)
        : "",
    });
  }, [offer, reset]);

 useEffect(() => {
  if (skipNextClear.current) {
    skipNextClear.current = false;
    return;
  }

  if (!validFrom) return;   // 👈 only clear when a date is actually selected

  setValue("valid_to", "");
}, [validFrom, setValue]);

  const onSubmit = async (values: OfferFormData) => {
    try {
      const payload = {
        description: values.description.trim(),
        valid_from: values.valid_from,
        valid_to: values.valid_to,
        discount_amount: Number(values.discount_amount),
        active:true
      };

      let response;

      if (offer) {
        response = await updateSpecialOffer(offer.id, payload);
      } else {
        response = await createSpecialOffer(payload);
      }

      if (response.status === 200 || response.status === 201) {
        toast.success(
          offer
            ? "Offer updated successfully"
            : "Offer created successfully"
        );
        onSuccess();
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 bg-gradient-to-r from-[#2d3440] to-[#535c6b]">
          <h2 className="text-2xl font-bold text-white">
            {offer ? "Edit Special Offer" : "Create Special Offer"}
          </h2>

          <button
            onClick={onClose}
            className="text-white hover:bg-white/10 p-2 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">

            {/* Description — full width */}
            <div className="md:col-span-2">
              <FormField
                key={`${offer?.id}-description`}
                control={control}
                name="description"
                label="Description"
                type="text"
                placeholder="Enter offer description"
                error={errors.description?.message}
                required
              />
            </div>

            <DatePicker
              key={`${offer?.id}-valid_from`}
              control={control}
              name="valid_from"
              label="Valid From"
              required
              error={errors.valid_from?.message}
              minDate={new Date().toISOString().split("T")[0]}
            />

            <DatePicker
              key={`${offer?.id}-valid_to`}
              control={control}
              name="valid_to"
              label="Valid To"
              required
              error={errors.valid_to?.message}
              minDate={
                validFrom || new Date().toISOString().split("T")[0]
              }
            />

            {/* Discount Amount — full width */}
            <div className="md:col-span-2">
              <FormField
                required
                key={`${offer?.id}-discount_amount`}
                control={control}
                name="discount_amount"
                label="Discount Amount (₹)"
                type="number"
                placeholder="Enter discount amount"
                error={errors.discount_amount?.message}
              />
            </div>
          </div>

          {/* Footer actions */}
          <div className="flex gap-3 border-t p-5">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl bg-gray-200 hover:bg-gray-300 font-semibold"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 py-3 rounded-xl bg-mlb-gold hover:bg-mlb-gold/90 text-brand-700 font-semibold"
            >
              {isSubmitting
                ? "Saving..."
                : offer
                  ? "Update Offer"
                  : "Create Offer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}